import React from 'react'

interface CodeDisplayProps {
  code: string
  highlightLines: number[]
}

interface Token {
  type: 'keyword' | 'string' | 'comment' | 'class' | 'function' | 'number' | 'operator' | 'text' | 'decorator'
  value: string
}

export const CodeDisplay: React.FC<CodeDisplayProps> = ({ code, highlightLines }) => {
  const lines = code.split('\n')

  // Tokenize a line of Python code
  const tokenizeLine = (line: string): Token[] => {
    const tokens: Token[] = []
    let remaining = line

    const keywords = ['import', 'from', 'async', 'await', 'def', 'class', 'return', 'if', 'else', 'elif', 'for', 'while', 'try', 'except', 'with', 'as', 'pass', 'break', 'continue', 'and', 'or', 'not', 'is', 'in', 'True', 'False', 'None', 'print', 'yield']
    const classNames = ['Agent', 'Model', 'Tool', 'Orchestrator', 'Workflow', 'Step', 'StreamingUI']

    while (remaining.length > 0) {
      // Check for decorator first
      if (remaining.match(/^@[a-zA-Z_][a-zA-Z0-9_]*/)) {
        const decoratorMatch = remaining.match(/^@[a-zA-Z_][a-zA-Z0-9_]*/)!
        tokens.push({ type: 'decorator', value: decoratorMatch[0] })
        remaining = remaining.slice(decoratorMatch[0].length)
        continue
      }

      // Check for comment
      if (remaining.startsWith('#')) {
        tokens.push({ type: 'comment', value: remaining })
        break
      }

      // Check for triple-quoted strings first
      const tripleDoubleMatch = remaining.match(/^"""[\s\S]*?"""/)
      if (tripleDoubleMatch) {
        tokens.push({ type: 'string', value: tripleDoubleMatch[0] })
        remaining = remaining.slice(tripleDoubleMatch[0].length)
        continue
      }

      const tripleSingleMatch = remaining.match(/^'''[\s\S]*?'''/)
      if (tripleSingleMatch) {
        tokens.push({ type: 'string', value: tripleSingleMatch[0] })
        remaining = remaining.slice(tripleSingleMatch[0].length)
        continue
      }

      // Check for f-strings
      const fstringMatch = remaining.match(/^f(["'])((?:\\.|(?!\1)[^\\])*)\1/)
      if (fstringMatch) {
        tokens.push({ type: 'string', value: fstringMatch[0] })
        remaining = remaining.slice(fstringMatch[0].length)
        continue
      }

      // Check for strings (single or double quotes)
      const doubleStringMatch = remaining.match(/^"((?:\\.|[^"\\])*)"/);
      if (doubleStringMatch) {
        tokens.push({ type: 'string', value: doubleStringMatch[0] })
        remaining = remaining.slice(doubleStringMatch[0].length)
        continue
      }

      const singleStringMatch = remaining.match(/^'((?:\\.|[^'\\])*)'/);
      if (singleStringMatch) {
        tokens.push({ type: 'string', value: singleStringMatch[0] })
        remaining = remaining.slice(singleStringMatch[0].length)
        continue
      }

      // Check for class names
      const classMatch = remaining.match(new RegExp(`^(${classNames.join('|')})\\b`))
      if (classMatch) {
        tokens.push({ type: 'class', value: classMatch[0] })
        remaining = remaining.slice(classMatch[0].length)
        continue
      }

      // Check for keywords
      const keywordMatch = remaining.match(new RegExp(`^(${keywords.join('|')})\\b`))
      if (keywordMatch) {
        tokens.push({ type: 'keyword', value: keywordMatch[0] })
        remaining = remaining.slice(keywordMatch[0].length)
        continue
      }

      // Check for function names (word followed by parenthesis)
      const funcMatch = remaining.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*(?=\()/)
      if (funcMatch) {
        tokens.push({ type: 'function', value: funcMatch[1] })
        remaining = remaining.slice(funcMatch[1].length)
        continue
      }

      // Check for numbers
      const numberMatch = remaining.match(/^\d+(\.\d+)?/)
      if (numberMatch) {
        tokens.push({ type: 'number', value: numberMatch[0] })
        remaining = remaining.slice(numberMatch[0].length)
        continue
      }

      // Check for operators
      const operatorMatch = remaining.match(/^(==|!=|<=|>=|->|[=+\-*/<>!&|%^~])/)
      if (operatorMatch) {
        tokens.push({ type: 'operator', value: operatorMatch[0] })
        remaining = remaining.slice(operatorMatch[0].length)
        continue
      }

      // Regular text (identifiers, whitespace, punctuation)
      const textMatch = remaining.match(/^[a-zA-Z_][a-zA-Z0-9_]*|^\s+|^./)
      if (textMatch) {
        tokens.push({ type: 'text', value: textMatch[0] })
        remaining = remaining.slice(textMatch[0].length)
        continue
      }

      // Fallback - take one character
      tokens.push({ type: 'text', value: remaining[0] })
      remaining = remaining.slice(1)
    }

    return tokens
  }

  // Catppuccin Mocha-inspired color scheme
  const getTokenStyle = (type: Token['type']): string => {
    switch (type) {
      case 'keyword':
        return 'text-[#cba6f7]' // Mauve - purple for keywords
      case 'string':
        return 'text-[#a6e3a1]' // Green
      case 'comment':
        return 'text-[#6c7086] italic' // Overlay0 - muted gray
      case 'class':
        return 'text-[#89dceb]' // Sky - cyan for classes
      case 'function':
        return 'text-[#89b4fa]' // Blue
      case 'number':
        return 'text-[#fab387]' // Peach - orange
      case 'operator':
        return 'text-[#94e2d5]' // Teal
      case 'decorator':
        return 'text-[#f5c2e7]' // Pink
      default:
        return 'text-[#cdd6f4]' // Text - light gray
    }
  }

  return (
    <div className="relative h-full bg-[#1e1e2e] code-scrollbar">
      <pre className="text-sm leading-relaxed p-4 overflow-auto h-full font-mono">
        <code>
          {lines.map((line, index) => {
            const lineNumber = index + 1
            const isHighlighted = highlightLines.includes(lineNumber)
            const tokens = tokenizeLine(line)

            return (
              <div
                key={index}
                className={`
                  flex min-h-[1.5rem]
                  ${isHighlighted ? 'bg-[#f9e2af]/20 border-l-4 border-[#f9e2af] -ml-1 pl-1' : ''}
                  transition-all duration-200
                `}
              >
                <span
                  className={`
                    inline-block w-10 pr-4 text-right select-none flex-shrink-0
                    ${isHighlighted ? 'text-[#f9e2af] font-bold' : 'text-[#6c7086]'}
                  `}
                >
                  {lineNumber}
                </span>
                <span className="flex-1 whitespace-pre">
                  {tokens.length === 0 ? ' ' : tokens.map((token, tokenIndex) => (
                    <span key={tokenIndex} className={getTokenStyle(token.type)}>
                      {token.value}
                    </span>
                  ))}
                </span>
              </div>
            )
          })}
        </code>
      </pre>

      {/* Active line indicator */}
      {highlightLines.length > 0 && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 bg-[#f9e2af]/20 text-[#f9e2af] text-xs rounded font-medium border border-[#f9e2af]/30">
            Line {highlightLines[highlightLines.length - 1]}
          </span>
        </div>
      )}

      {/* Custom scrollbar styles */}
      <style>{`
        .code-scrollbar pre::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .code-scrollbar pre::-webkit-scrollbar-track {
          background: #181825;
          border-radius: 4px;
        }
        .code-scrollbar pre::-webkit-scrollbar-thumb {
          background: #45475a;
          border-radius: 4px;
        }
        .code-scrollbar pre::-webkit-scrollbar-thumb:hover {
          background: #585b70;
        }
        .code-scrollbar pre::-webkit-scrollbar-corner {
          background: #181825;
        }
      `}</style>
    </div>
  )
}